// src/components/chatbot/ChatMessages.tsx
import React, { useRef, useEffect } from 'react';
import { Bot } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Message, SuggestionItem } from './types';
import ChatMessage from './ChatMessage';

type ChatMessagesProps = {
  messages: Message[];
  isTyping: boolean;
  botAvatar: string;
  botName: string;
  onSuggestionClick: (suggestion: SuggestionItem) => void;
};

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  isTyping,
  botAvatar,
  botName,
  onSuggestionClick,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const firstResponseHandledRef = useRef(false);
  
  // Auto-scroll to the bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages, isTyping]);
  
  // Find the index of the last bot message
  const lastBotMessageIndex = [...messages].reverse().findIndex(msg => msg.sender === 'bot');
  const lastBotMessageActualIndex = lastBotMessageIndex !== -1 
    ? messages.length - 1 - lastBotMessageIndex 
    : -1;
  
  // Process the messages to handle first-response duplication
  let messagesToRender = [...messages];
  
  // Only run this logic once on the first set of messages that might have duplicates
  if (!firstResponseHandledRef.current && messages.length >= 3) {
    // Check for duplicate first responses (when a user sends first message, bot responds twice)
    let hasDuplicateFirstResponse = false;
    
    // First check if we have a sequence like: [bot welcome] [user message] [bot response] [identical bot response]
    if (messages.length >= 4 && 
        messages[0].sender === 'bot' && 
        messages[1].sender === 'user' && 
        messages[2].sender === 'bot' && 
        messages[3].sender === 'bot' && 
        messages[2].content === messages[3].content) {
      hasDuplicateFirstResponse = true;
      // Remove the duplicate (keep the later one with potentially more metadata)
      messagesToRender = [
        messages[0], // Welcome message
        messages[1], // User message
        // messages[2], // Skip the first duplicate
        messages[3], // Keep the second duplicate
        ...messages.slice(4) // Keep any other messages
      ];
    } 
    // Check for a simpler pattern: [user message] [bot response] [identical bot response]
    else if (messages.length >= 3 && 
             messages[0].sender === 'user' && 
             messages[1].sender === 'bot' && 
             messages[2].sender === 'bot' && 
             messages[1].content === messages[2].content) {
      hasDuplicateFirstResponse = true;
      // Remove the duplicate (keep the later one with potentially more metadata)
      messagesToRender = [
        messages[0], // User message
        // messages[1], // Skip the first duplicate
        messages[2], // Keep the second duplicate
        ...messages.slice(3) // Keep any other messages
      ];
    }
    
    // Mark as handled to avoid applying this logic repeatedly
    if (hasDuplicateFirstResponse) {
      firstResponseHandledRef.current = true;
    }
  }
  
  return (
    <div 
      className="flex-1 overflow-y-auto p-4 space-y-4 bg-card"
      ref={scrollAreaRef}
    >
      {messagesToRender.map((message, index) => (
        <ChatMessage
          key={message.id}
          message={message}
          botAvatar={botAvatar}
          botName={botName}
          onSuggestionClick={onSuggestionClick}
          isLastBotMessage={message.sender === 'bot' && index === lastBotMessageActualIndex}
        />
      ))}
      
      {/* Typing indicator */}
      {isTyping && (
        <div className="flex justify-start">
          <Avatar className="h-8 w-8 mr-2 flex-shrink-0 mt-1 bg-primary/10">
            <AvatarImage src={botAvatar} alt={botName} />
            <AvatarFallback className="bg-primary/10">
              <Bot className="h-4 w-4 text-primary" />
            </AvatarFallback>
          </Avatar>
          <div className="bg-muted p-3 rounded-lg rounded-tl-none">
            <div className="flex space-x-1">
              <div className="h-2 w-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="h-2 w-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '200ms' }}></div>
              <div className="h-2 w-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '400ms' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessages;